import { Platform } from '../types/Platform';
import { AnnotationTranscript } from '../types/AnnotationModel';
import { assertResponse } from './Utils';

// The newest few games are imported inline and land before the POST resolves (ImportedNow); the rest
// are a background job (JobId, awaited with waitForImport). `groupId` targets a group; `from`
// (yyyy-mm-dd) re-imports older games from a date.
// groupId/groupToken in the result name where the games actually landed (My Space when none was
// sent). Verify and navigate with these, never the requested id.
export interface ImportTarget {
  groupId: number | null;
  groupToken: string | null;
}

export interface StartImportResult extends ImportTarget {
  jobId: number;
  importedNow: number;
}

async function startImportPlatform(
  username: string,
  platform: Platform,
  groupId?: number | null,
  from?: string,
): Promise<StartImportResult> {
  const query = new URLSearchParams({ username, platform: String(platform) });
  if (groupId != null) {
    query.append('groupId', String(groupId));
  }
  if (from) {
    query.append('from', from);
  }
  const response = await fetch('/api/Import?' + query.toString(), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    // A missing handle or wrong platform comes back as a player-facing Error; surface it.
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.Error) {
        message = body.Error;
      }
    } catch {
      /* no JSON body — keep the status line */
    }
    throw new Error(message);
  }
  const data = await response.json();
  return {
    jobId: data.JobId as number,
    importedNow: (data.ImportedNow as number) ?? 0,
    groupId: (data.GroupId as number) ?? null,
    groupToken: (data.GroupToken as string) ?? null,
  };
}

async function getImportStatus(jobId: number): Promise<{ status: string; error?: string }> {
  const response = await fetch('/api/Import/status?jobId=' + jobId);
  await assertResponse(response);
  const data = await response.json();
  return { status: data.Status as string, error: data.Error as string | undefined };
}

// Polls the job until it finishes. Rejects on Failed, and on timeout, so a wedged job tells the
// player to check back instead of quietly giving up.
async function waitForImport(
  jobId: number,
  { intervalMs = 2000, timeoutMs = 300000 }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const { status, error } = await getImportStatus(jobId);
    if (status === 'Completed') {
      return;
    }
    if (status === 'Failed') {
      // Surface the job's recorded reason (usually something the player can fix).
      throw new Error(error && error !== '' ? error : 'Import failed');
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(
    'Your import is taking longer than usual. It may still be running. Check back in a moment.',
  );
}

// The candidate a scoresheet photo produced. Not saved: on confirm the caller submits Pgn through
// startImportPgn.
export interface PhotoReadResult {
  Pgn: string;
  Movetext: string;
  Warnings: string[];
  MovesRead: number;
  MovesRequested: number;
  Truncated: boolean;
  White?: string;
  Black?: string;
  Date?: string;
  Result?: string;
}

// Reads scoresheet photos into a candidate game. Multipart, so don't set Content-Type by hand.
// Surfaces the server's Error message.
async function readScoresheet(files: File[]): Promise<PhotoReadResult> {
  const form = new FormData();
  files.forEach((f) => form.append('images', f));
  const response = await fetch('/api/Import/photo', { method: 'POST', body: form });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.Error) {
        message = body.Error;
      }
    } catch {
      /* no JSON body — keep the status line */
    }
    throw new Error(message);
  }
  return response.json();
}

// Raw OCR read for the interactive reader: moves exactly as written, repaired on a board before saving.
async function transcribeScoresheet(files: File[]): Promise<AnnotationTranscript> {
  const form = new FormData();
  files.forEach((f) => form.append('images', f));
  const response = await fetch('/api/Import/photo/transcribe', { method: 'POST', body: form });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.Error) {
        message = body.Error;
      }
    } catch {
      /* no JSON body — keep the status line */
    }
    throw new Error(message);
  }
  return response.json();
}

async function startImportPgn(
  pgn: string,
  groupId?: number | null,
): Promise<ImportTarget> {
  const response = await fetch('/api/Import/importPgn', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ pgn: pgn, groupId: groupId ?? null }),
  });
  await assertResponse(response);
  const data = await response.json();
  return {
    groupId: (data.GroupId as number) ?? null,
    groupToken: (data.GroupToken as string) ?? null,
  };
}
export default {
  startImportPlatform,
  waitForImport,
  startImportPgn,
  readScoresheet,
  transcribeScoresheet,
};
